import _MomentsMarkSeenMutation from "../__generated__/MomentsMarkSeenMutation.graphql";
import _MomentsQuery from "../__generated__/MomentsQuery.graphql";
import { useEffect, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle2, GraduationCap, Languages, MessageCircle, MessagesSquare, Quote, Smile, Sparkles, Timer, type LucideIcon } from 'lucide-react';
import { En } from '../i18n/En';
import { formatDate } from '../i18n/format';
import { t } from '../i18n/t';
import type { Register } from '../profile/register';
import { newestAt, unseen, type MilestoneRow } from './milestones.ts';
import { patternLabel } from './practice.ts';
import type { MomentsMarkSeenMutation } from '../__generated__/MomentsMarkSeenMutation.graphql';
import type { MomentsQuery as MomentsQueryType } from '../__generated__/MomentsQuery.graphql';
const MomentsQuery = (_MomentsQuery.hash && _MomentsQuery.hash !== "2596ef2daed4233f24d8e1caa6eea204" && console.error("The definition of 'MomentsQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _MomentsQuery);
const MarkSeen = (_MomentsMarkSeenMutation.hash && _MomentsMarkSeenMutation.hash !== "c32cc460b1b49a419413ed1c08c09c01" && console.error("The definition of 'MomentsMarkSeenMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _MomentsMarkSeenMutation);
const ICON: Record<string, LucideIcon> = {
  FIRST_CONVERSATION: MessageCircle,
  CONVERSATIONS: MessagesSquare,
  LONG_TALK: Timer,
  LONG_ANSWER: Quote,
  NEW_TUTOR: Smile,
  PATTERN_USED: CheckCircle2,
  WORDS: Languages,
  LESSON_SET: GraduationCap,
  FIRST_BOOK: BookOpen
};
const STICKER: Record<string, string> = {
  FIRST_CONVERSATION: '🎉',
  LONG_TALK: '⏱️',
  LONG_ANSWER: '💬',
  NEW_TUTOR: '🤝',
  FIRST_BOOK: '📖'
};
function title(m: MilestoneRow): string {
  const count = Number(m.detail ?? 0);
  switch (m.kind) {
    case 'FIRST_CONVERSATION':
      return t('moment-first-conversation');
    case 'CONVERSATIONS':
      return t('moment-conversations', {
        count
      });
    case 'LONG_TALK':
      return t('moment-long-talk');
    case 'LONG_ANSWER':
      return t('moment-long-answer');
    case 'NEW_TUTOR':
      return t('moment-new-tutor');
    case 'PATTERN_USED':
      return t('moment-pattern-used');
    case 'WORDS':
      return t('moment-words', {
        count
      });
    case 'LESSON_SET':
      return t('moment-lesson-set');
    default:
      return t('moment-first-book');
  }
}
function Detail({
  m
}: {
  m: MilestoneRow;
}) {
  if (!m.detail || m.kind === 'CONVERSATIONS' || m.kind === 'WORDS') return null;
  // A pattern's label is in the display language; a book, a set and a
  // tutor's name are the English they were given.
  return <span className="text-base-content/70 text-sm">
      {m.kind === 'PATTERN_USED' ? patternLabel(m.detail) : <En>{m.detail}</En>}
    </span>;
}
function when(m: MilestoneRow): string {
  return formatDate(new Date(m.achievedAt), {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
function Row({
  m
}: {
  m: MilestoneRow;
}) {
  const Icon = ICON[m.kind] ?? Sparkles;
  return <li className="flex items-start gap-3">
      <span aria-hidden className="bg-primary/10 text-primary grid size-10 shrink-0 place-items-center rounded-full">
        <Icon className="size-5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-medium">{title(m)}</span>
        <Detail m={m} />
        <span className="text-base-content/50 text-xs">{when(m)}</span>
      </span>
    </li>;
}
function Sticker({
  m
}: {
  m: MilestoneRow;
}) {
  return <li className="bg-base-100 border-base-200 flex flex-col items-center gap-1 rounded-box border p-3 text-center">
      <span aria-hidden className="text-4xl leading-none">
        {STICKER[m.kind] ?? '⭐'}
      </span>
      <span className="text-sm font-medium">{title(m)}</span>
      <Detail m={m} />
    </li>;
}
const FIRST_SHOWN = 5;

/** Newest first. No locked slots, nothing "to go" (milestones design §2). */
export function MomentsList({
  register
}: {
  register: Register;
}) {
  const data = useLazyLoadQuery<MomentsQueryType>(MomentsQuery, {}, {
    fetchPolicy: 'store-and-network'
  });
  const [all, setAll] = useState(false);
  const rows = data.me?.learner?.milestones ?? [];
  if (rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, FIRST_SHOWN);
  return <section className="flex flex-col gap-3">
      <div className="flex flex-col">
        <h2 className="font-semibold">{t('moments-title')}</h2>
        <span className="text-base-content/60 text-sm">{t('moments-intro')}</span>
      </div>
      {register === 'kid' ? <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {shown.map(m => <Sticker key={`${m.kind}-${m.detail ?? ''}`} m={m} />)}
        </ul> : <ul className="bg-base-100 border-base-200 flex flex-col gap-4 rounded-box border p-4">
          {shown.map(m => <Row key={`${m.kind}-${m.detail ?? ''}`} m={m} />)}
        </ul>}
      {!all && rows.length > FIRST_SHOWN && <button type="button" className="link link-primary min-h-(--hit-min) self-start text-sm" onClick={() => setAll(true)}>
          {t('moments-show-all', {
        count: rows.length
      })}
        </button>}
    </section>;
}

/** The newest moment since this browser last looked, until dismissed. */
export function NewMoment({
  register
}: {
  register: Register;
}) {
  const data = useLazyLoadQuery<MomentsQueryType>(MomentsQuery, {}, {
    fetchPolicy: 'store-and-network'
  });
  const rows = data.me?.learner?.milestones ?? [];
  const [markSeen] = useMutation<MomentsMarkSeenMutation>(MarkSeen);
  // Ahead of the store while the mutation is in flight, so the card goes at once.
  const [dismissedAt, setDismissedAt] = useState<string | null>(null);
  const seenAt = dismissedAt ?? data.me?.learner?.milestonesSeenAt ?? null;
  const fresh = unseen(rows, seenAt);
  const newest = newestAt(rows);
  const mark = (at: string) => {
    setDismissedAt(at);
    markSeen({
      variables: {
        at
      }
    });
  };
  useEffect(() => {
    if (seenAt !== null || newest === null) return;
    setDismissedAt(newest);
    markSeen({
      variables: {
        at: newest
      }
    });
  }, [seenAt, newest, markSeen]);
  const top = fresh[0];
  if (!top) return null;
  const dismiss = () => {
    if (newest) mark(newest);
  };
  return <section role="status" className="rise border-warning/40 bg-warning/10 flex flex-col gap-3 rounded-box border-2 p-4">
      <span className="text-warning-content/80 flex items-center gap-1 text-xs font-semibold tracking-wide uppercase">
        <Sparkles aria-hidden className="size-4" />
        {t('moments-new')}
      </span>
      <div className="flex items-center gap-3">
        <span aria-hidden className="text-4xl leading-none">
          {register === 'kid' ? STICKER[top.kind] ?? '⭐' : '🎉'}
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="text-lg font-bold">{title(top)}</span>
          <Detail m={top} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn btn-primary btn-sm min-h-(--hit-min)" onClick={dismiss}>
          {t('moments-nice')}
        </button>
        {fresh.length > 1 && register === 'adult' && <Link to="/history" className="link text-sm" onClick={dismiss}>
            {t('moments-new-more', {
          count: fresh.length - 1
        })}
          </Link>}
      </div>
    </section>;
}